<script>
    import { onMount } from "svelte";
    import { SelectedSnippetsStore } from "$/stores/SelectedSnippetsStore.js";
    import { getRootDocument } from "$/utils/Utils";

    export let card;

    let shiftPressed = false;
    let selected;

    function handleKeyDown(event) {
        shiftPressed = false;
        if (event.key === "Shift" && !isInputElement(event.target))
            shiftPressed = true;
    }

    function handleKeyUp(event) {
        //NOTE: event.shiftKey is not reliable so we use event.key === 'Shift'
        if (event.key === "Shift") shiftPressed = false;
    }

    function handleClick(event) {
        if (isInputElement(event.target) && shiftPressed) shiftPressed = false;
    }

    function isInputElement(target) {
        return target.tagName === "INPUT" || target.isContentEditable;
    }

    function handleBlur(event) {
        shiftPressed = false;
    }

    onMount(() => {
        /* 
            Events like keydown/keyup are only fired on main window or document, not on a shadow root. 
            And adding listeners to the shadow root (getRootDocument()) will not catch keyboard events 
            unless the shadow root or its children are focused and the event is not stopped.
            Since most of the sites have a main window keyboard event listener
            We must add the listeners to the main window.
        */
        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("keyup", handleKeyUp);
        getRootDocument().addEventListener("blur", handleBlur);
        getRootDocument().addEventListener("click", handleClick);

        selected = false;

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("keyup", handleKeyUp);
            getRootDocument().removeEventListener("blur", handleBlur);
            getRootDocument().removeEventListener("click", handleClick);
            unsubscribeSelectedSnippetsStore();
        };
    });

    $: selected, updateSelectedSnippetsCount();

    const updateSelectedSnippetsCount = () => {
        if (selected) {
            SelectedSnippetsStore.update((snippets) => [...snippets, card]);
        } else {
            SelectedSnippetsStore.update((snippets) =>
                snippets?.filter((snippet) => snippet.id !== card.id),
            );
        }
    };

    const unsubscribeSelectedSnippetsStore = SelectedSnippetsStore.subscribe(
        (snippets) => {
            if (!snippets?.find((snippet) => snippet.id === card.id))
                selected = false;
        },
    );
</script>

<!-- checkbox -->
<label
    class="card-selector"
    class:card-selector-hidden={// hide checkbox if shift is not pressed and there are no selected snippets
    !shiftPressed && $SelectedSnippetsStore?.length === 0}
>
    <input type="checkbox" bind:checked={selected} />
    <svg
        class="checkmark-svg"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
    >
        <path
            fill-rule="evenodd"
            d="M19.916 4.626a.75.75 0 01.208 1.04l-9 13.5a.75.75 0 01-1.154.114l-6-6a.75.75 0 011.06-1.06l5.353 5.353 8.493-12.739a.75.75 0 011.04-.208z"
            clip-rule="evenodd"
        />
    </svg>
</label>

<style>
    .card-selector {
        @apply reset relative h-3 w-3 cursor-pointer rounded-sm border border-solid border-gray-400 bg-white !important;
    }

    .card-selector-hidden {
        @apply hidden !important;
    }

    .card-selector:checked {
        @apply reset border-blue-500 bg-blue-500 text-blue-900 !important;
    }

    .card-selector input[type="checkbox"] {
        @apply reset absolute opacity-0 !important;
    }

    .checkmark-svg {
        @apply reset absolute left-0 top-0 rounded-sm border border-gray-400 opacity-0 !important;
    }

    .card-selector input[type="checkbox"]:checked ~ .checkmark-svg {
        @apply reset absolute left-0 top-0 rounded-sm border border-gray-400 fill-black opacity-100 !important;
    }
</style>
